import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import {
	Card,
	CardBody,
	CardHeader,
} from "@tql/ui-components-react/kendo/layout";
import {
	calendarCheck_01,
	clockFastForward,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useMemo } from "react";

import type { ShipmentStats } from "~/shared/schemas/loads-schema";

import { useKeyDown } from "../../hooks/use-key-down.ts";
import {
	displayValue,
	extractExceptions,
} from "../../utils/summary-card-utils.ts";
import { SummaryStat } from "../summary-stat/summary-stat.tsx";
import styles from "./load-summary-cards.module.css";

export const LoadSummaryCards = ({
	summary,
	onClickSummaryCard,
	isLoading,
	activeCardType,
}: {
	summary: ShipmentStats;
	onClickSummaryCard: (status: string, needsImmediateAction: boolean) => void;
	isLoading: boolean;
	activeCardType: string | null;
}) => {
	const {
		todaysShipmentsNotDispatched,
		todaysShipmentsDispatched,
		todaysShipmentsInTransit,
		todaysShipmentsDelivered,
		futureShipmentsNotDispatched,
		futureShipmentsDispatched,
	} = summary;

	const exceptionCounts = useMemo(
		() => ({
			notDispatched: extractExceptions(todaysShipmentsNotDispatched),
			dispatched: extractExceptions(todaysShipmentsDispatched),
			inTransit: extractExceptions(todaysShipmentsInTransit),
			delivered: extractExceptions(todaysShipmentsDelivered),
		}),
		[
			todaysShipmentsNotDispatched,
			todaysShipmentsDispatched,
			todaysShipmentsInTransit,
			todaysShipmentsDelivered,
		],
	);

	const handleCardClick = useCallback(
		(status: string, needsImmediateAction: boolean) => {
			onClickSummaryCard?.(status, needsImmediateAction);
		},
		[onClickSummaryCard],
	);

	const { handleKeyDown } = useKeyDown(handleCardClick);

	const createClickHandler = useCallback(
		(status: string, needsImmediateAction: boolean) => () =>
			handleCardClick(status, needsImmediateAction),
		[handleCardClick],
	);

	return (
		<div className={styles.loadSummaryCardsContainer}>
			<Card className={styles.mainCard}>
				<CardHeader>
					<SvgIcon icon={calendarCheck_01} size={"xxlarge"} />
					Today&apos;s Shipments
				</CardHeader>
				<CardBody>
					<div className={styles.row}>
						<SummaryStat
							cardType={"notDispatched"}
							createClickHandler={createClickHandler}
							displayValue={displayValue}
							exceptionCounts={exceptionCounts}
							handleKeyDown={handleKeyDown}
							isActive={activeCardType === "notDispatched"}
							isLoading={isLoading}
							onClickSummaryCard={onClickSummaryCard}
							summary={summary}
							todaysShipmentsNotDispatched={todaysShipmentsNotDispatched}
						/>
						<SummaryStat
							cardType={"dispatched"}
							createClickHandler={createClickHandler}
							displayValue={displayValue}
							exceptionCounts={exceptionCounts}
							handleKeyDown={handleKeyDown}
							isActive={activeCardType === "dispatched"}
							isLoading={isLoading}
							onClickSummaryCard={onClickSummaryCard}
							summary={summary}
							todaysShipmentsNotDispatched={todaysShipmentsDispatched}
						/>
						<SummaryStat
							cardType={"inTransit"}
							createClickHandler={createClickHandler}
							displayValue={displayValue}
							exceptionCounts={exceptionCounts}
							handleKeyDown={handleKeyDown}
							isActive={activeCardType === "inTransit"}
							isLoading={isLoading}
							onClickSummaryCard={onClickSummaryCard}
							summary={summary}
							todaysShipmentsNotDispatched={todaysShipmentsInTransit}
						/>
						<SummaryStat
							cardType={"delivered"}
							createClickHandler={createClickHandler}
							displayValue={displayValue}
							exceptionCounts={exceptionCounts}
							handleKeyDown={handleKeyDown}
							isActive={activeCardType === "delivered"}
							isLoading={isLoading}
							onClickSummaryCard={onClickSummaryCard}
							summary={summary}
							todaysShipmentsNotDispatched={todaysShipmentsDelivered}
						/>
					</div>
				</CardBody>
			</Card>
			<Card className={styles.sideCard}>
				<CardHeader>
					<SvgIcon icon={clockFastForward} size={"xxlarge"} />
					Future Shipments
				</CardHeader>
				<CardBody>
					<div className={styles.row}>
						<SummaryStat
							cardType={"futureNotDispatched"}
							createClickHandler={createClickHandler}
							displayValue={displayValue}
							exceptionCounts={exceptionCounts}
							handleKeyDown={handleKeyDown}
							isActive={activeCardType === "futureNotDispatched"}
							isLoading={isLoading}
							onClickSummaryCard={onClickSummaryCard}
							summary={summary}
							todaysShipmentsNotDispatched={{
								count: futureShipmentsNotDispatched,
								exceptionsCount: 0,
							}}
						/>
						<SummaryStat
							cardType={"futureDispatched"}
							createClickHandler={createClickHandler}
							displayValue={displayValue}
							exceptionCounts={exceptionCounts}
							handleKeyDown={handleKeyDown}
							isActive={activeCardType === "futureDispatched"}
							isLoading={isLoading}
							onClickSummaryCard={onClickSummaryCard}
							summary={summary}
							todaysShipmentsNotDispatched={{
								count: futureShipmentsDispatched,
								exceptionsCount: 0,
							}}
						/>
					</div>
				</CardBody>
			</Card>
		</div>
	);
};
